// useVisualLayers — fetches visual-layers summary + the most-recent
// satellite capture (with bbox) for a site, in parallel. Used by
// FloorStackContainer to decide whether to render the 鳥瞰層 tier.
//
// docs/specs/spatial-hierarchy-v2.md §6.1

import { useCallback, useEffect, useState } from "react";
import {
  getVisualLayers, getSiteCaptures,
  friendlyVisualLayersError,
  type OutdoorQuality, type SatelliteCapture,
} from "../../lib/visualLayersApi.js";

export interface AerialAvailability {
  /** True when site has at least one map_layouts row with entity_type='site'. */
  has_aerial: boolean;
  /** Highest-resolution kind currently present (drives the badge text). */
  quality: OutdoorQuality;
  /** Most recent satellite capture row (with bbox + dimensions) — null
   *  when no satellite capture exists, even if a 3D model_3d layer is
   *  present (3D rendering is PR-F's job, not PR-E's). */
  latest_capture: SatelliteCapture | null;
}

export type VisualLayersState =
  | { status: "loading" }
  | { status: "ready"; data: AerialAvailability }
  | { status: "error"; message: string };

export function useVisualLayers(siteId: string | null, tenantId: string | null) {
  const [state, setState] = useState<VisualLayersState>({ status: "loading" });

  const reload = useCallback(async () => {
    if (!siteId) return;
    try {
      const [layers, captures] = await Promise.all([
        getVisualLayers(siteId, tenantId),
        getSiteCaptures(siteId, tenantId).catch(() => [] as SatelliteCapture[]),
      ]);
      // Latest capture = first row (endpoint already orders DESC by created_at).
      // Prefer the latest source_kind='satellite_hd' over 'satellite_capture' when
      // both exist on the same site; falls back to raw when no HD exists.
      const hd = captures.find((c) => c.source_kind === "satellite_hd");
      const raw = captures.find((c) => c.source_kind === "satellite_capture");
      const latestCapture = hd ?? raw ?? captures[0] ?? null;
      setState({
        status: "ready",
        data: {
          has_aerial: layers.outdoor_quality !== "none",
          quality: layers.outdoor_quality,
          latest_capture: latestCapture,
        },
      });
    } catch (err) {
      setState({ status: "error", message: friendlyVisualLayersError(err) });
    }
  }, [siteId, tenantId]);

  useEffect(() => { void reload(); }, [reload]);

  return { state, reload };
}
